import { useState, useEffect, useCallback } from 'react';

import { logEvent } from '../services/loggingService';
import { updateGtagConsent } from '../utils/gtag';

export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export interface CookieConsentState {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  consentDate: string | null;
  consentVersion: string;
  hasInteracted: boolean;
}

export interface CookieCategoryInfo {
  id: CookieCategory;
  name: string;
  description: string;
  required: boolean;
  cookies: Array<{
    name: string;
    purpose: string;
    duration: string;
    provider: string;
  }>;
}

const CONSENT_VERSION = '1.0.0';
const CONSENT_STORAGE_KEY = 'cookie-consent-preferences';
const CONSENT_EXPIRY_DAYS = 365; // 1 year

// Default consent state - only necessary cookies are enabled by default
const defaultConsentState: CookieConsentState = {
  necessary: true, // Always true as these are essential
  functional: false,
  analytics: false,
  marketing: false,
  consentDate: null,
  consentVersion: CONSENT_VERSION,
  hasInteracted: false,
};

// Cookie categories information
export const cookieCategoriesInfo: CookieCategoryInfo[] = [
  {
    id: 'necessary',
    name: 'Necessary',
    description: 'These cookies are essential for the website to function properly and cannot be disabled.',
    required: true,
    cookies: [
      {
        name: 'sb-auth-token',
        purpose: 'Authentication',
        duration: 'Session',
        provider: 'Supabase',
      },
      {
        name: 'cookie-consent-preferences',
        purpose: 'Stores your cookie consent preferences',
        duration: '1 year',
        provider: 'GoGrabJob',
      },
    ],
  },
  {
    id: 'functional',
    name: 'Functional',
    description: 'These cookies enable personalized features and functionality.',
    required: false,
    cookies: [
      {
        name: 'user-preferences',
        purpose: 'Remembers your preferences and settings',
        duration: '1 year',
        provider: 'GoGrabJob',
      },
      {
        name: 'recently-viewed',
        purpose: 'Tracks recently viewed items',
        duration: '30 days',
        provider: 'GoGrabJob',
      },
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'These cookies help us understand how visitors interact with our website.',
    required: false,
    cookies: [
      {
        name: '_ga',
        purpose: 'Used to distinguish users',
        duration: '2 years',
        provider: 'Google Analytics',
      },
      {
        name: '_gid',
        purpose: 'Used to distinguish users',
        duration: '24 hours',
        provider: 'Google Analytics',
      },
      {
        name: '_clck',
        purpose: 'Persists Clarity user ID',
        duration: '1 year',
        provider: 'Microsoft Clarity',
      },
      {
        name: '_clsk',
        purpose: 'Connects multiple page views into a session',
        duration: '1 day',
        provider: 'Microsoft Clarity',
      },
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'These cookies are used to track visitors across websites to display relevant advertisements.',
    required: false,
    cookies: [
      {
        name: '_fbp',
        purpose: 'Used by Facebook to deliver advertisements',
        duration: '3 months',
        provider: 'Facebook',
      },
      {
        name: '_gcl_au',
        purpose: 'Used by Google AdSense for experimenting with advertisement efficiency',
        duration: '3 months',
        provider: 'Google',
      },
    ],
  },
];

export const useCookieConsent = () => {
  const [consentState, setConsentState] = useState<CookieConsentState>(defaultConsentState);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);

  // Load consent preferences from localStorage
  useEffect(() => {
    const loadConsentPreferences = () => {
      try {
        const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
        
        if (storedConsent) {
          const parsedConsent = JSON.parse(storedConsent) as CookieConsentState;
          
          // Check if consent version matches current version
          if (parsedConsent.consentVersion !== CONSENT_VERSION) {
            // Version mismatch - need to re-consent
            setShowBanner(true);
            return defaultConsentState;
          }
          
          // Check if consent has expired (1 year)
          if (parsedConsent.consentDate) {
            const consentDate = new Date(parsedConsent.consentDate);
            const expiryDate = new Date(consentDate);
            expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
            
            if (new Date() > expiryDate) {
              // Consent expired - need to re-consent
              setShowBanner(true);
              return defaultConsentState;
            }
          }
          
          // Valid consent - use stored preferences
          setShowBanner(!parsedConsent.hasInteracted);
          return parsedConsent;
        } else {
          // No stored consent - show banner
          setShowBanner(true);
          return defaultConsentState;
        }
      } catch (error) {
        console.error('Error loading cookie consent preferences:', error);
        setShowBanner(true);
        return defaultConsentState;
      }
    };
    
    setConsentState(loadConsentPreferences());
  }, []);

  // Save consent preferences to localStorage
  const saveConsentPreferences = useCallback((preferences: CookieConsentState) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
      
      // Log consent for GDPR compliance
      try {
        logEvent({
          level: 'info',
          action: 'cookie_consent_updated',
          details: {
            preferences,
            timestamp: new Date().toISOString(),
          }
        });
      } catch (error) {
        console.error('Failed to log cookie consent:', error);
      }
    } catch (error) {
      console.error('Error saving cookie consent preferences:', error);
    }
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newState: CookieConsentState = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
      hasInteracted: true,
    };
    
    setConsentState(newState);
    saveConsentPreferences(newState);
    setShowBanner(false);
    setShowPreferenceModal(false);
    
    // Notify Google Analytics of consent update
    updateGtagConsent({ analytics: true, marketing: true });
  }, [saveConsentPreferences]);

  // Reject all non-necessary cookies
  const rejectAll = useCallback(() => {
    const newState: CookieConsentState = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
      hasInteracted: true,
    };
    
    setConsentState(newState);
    saveConsentPreferences(newState);
    setShowBanner(false);
    setShowPreferenceModal(false);
    
    // Notify Google Analytics of consent update (denied)
    updateGtagConsent({ analytics: false, marketing: false });
  }, [saveConsentPreferences]);

  // Save current preferences
  const savePreferences = useCallback(() => {
    const newState: CookieConsentState = {
      ...consentState,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
      hasInteracted: true,
    };
    
    setConsentState(newState);
    saveConsentPreferences(newState);
    setShowBanner(false);
    setShowPreferenceModal(false);
    
    // Notify Google Analytics of consent update based on user's choices
    updateGtagConsent({ analytics: consentState.analytics, marketing: consentState.marketing });
  }, [consentState, saveConsentPreferences]);

  // Update a specific cookie category
  const updateCookiePreference = useCallback((category: CookieCategory, value: boolean) => {
    // Don't allow necessary cookies to be disabled
    if (category === 'necessary' && !value) {
      return;
    }
    
    setConsentState(prev => ({
      ...prev,
      [category]: value,
    }));
  }, []);

  // Open preferences modal
  const openPreferenceModal = useCallback(() => {
    setShowPreferenceModal(true);
  }, []);

  // Close preferences modal
  const closePreferenceModal = useCallback(() => {
    setShowPreferenceModal(false);
  }, []);

  // Reset consent (for testing)
  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setConsentState(defaultConsentState);
    setShowBanner(true);
  }, []);

  return {
    consentState,
    showBanner,
    showPreferenceModal,
    acceptAll,
    rejectAll,
    savePreferences,
    updateCookiePreference,
    openPreferenceModal,
    closePreferenceModal,
    resetConsent,
  };
};
